import { useCallback } from 'react';

import { useLanguage } from '../../contexts/lang/languageContext';

import { useTenantSlug } from '~/features/tenant/helpers';
import { LocalizationService } from '~/services/api/localization/endpoints';
import { TenantService } from '~/services/api/tenant/endpoints';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

export const useLocalizedDepartmentData = () => {
  const slug = useTenantSlug();
  const { selectedLanguage } = useLanguage();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const departmentQuery = LocalizationService.endpoints.departments.useQuery(
    {
      selectedChannel: selectedChannel ?? 0,
      slug: slug,
    },
    { skip: !slug || !selectedChannel },
  );

  const getLocalizedDepartmentName = useCallback(
    (departmentName: string, translate = '') => {
      if (departmentQuery.isSuccess) {
        if (translate !== 'original') {
          const departmentFound = departmentQuery.data.find(
            (department) => department.name === departmentName,
          );
          if (departmentFound) {
            const translation =
              departmentFound.translations[selectedLanguage.code];
            return translation ? translation.name : departmentName;
          }
          return departmentName;
        } else {
          const departmentFound = departmentQuery.data.find(
            (department) =>
              department.translations[selectedLanguage.code]?.name ===
              departmentName,
          );
          if (departmentFound) {
            return departmentFound.name;
          } else {
            return departmentName;
          }
        }
      }
      return departmentName;
    },
    [departmentQuery.data, departmentQuery.isSuccess, selectedLanguage.code],
  );

  return { getLocalizedDepartmentName };
};

const useDepartmentData = () => {
  const slug = useTenantSlug();
  const selectedLanguage = useLanguage().selectedLanguage.id;

  const departmentQuery = TenantService.endpoints.departmentList.useQuery(
    {
      tenantSlug: slug,
      params: {
        language_id: selectedLanguage,
      },
    },
    { skip: !slug },
  );

  const departmentList = departmentQuery.data ?? [];

  return { departmentList };
};

export default useDepartmentData;
